import { Image as ImageIcon } from 'iconsax-react';
import type { ReactNode } from 'react';
import { type Control, type FieldPath, type FieldValues, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useToggle } from 'usehooks-ts';
import Flyout from '@/components/common/Flyout';
import { FlyoutMenuItem } from '@/components/common/Flyout/item';
import useFileSizeLimit from '@/hooks/files/use-file-size-limit';
import { usePickMediaFile } from '@/hooks/files/use-pick-media-file';
import usePreviewImage from '@/hooks/files/use-preview-image';
import useUserPlatform, { useIsDesktop } from '@/hooks/use-user-platform';
import { convertFileToUploadable, type Uploadable } from '@/models/uploadable';
import { addToast } from '@/stores/toast';
import { readableFileSize } from '@/utils/file-size';
import { FileType } from '@/utils/file-type';
import cn from '@/utils/merge-class-names';

interface Props<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>> {
  disabled?: boolean;
  name: TName;
  control: Control<TFieldValues>;
  className?: string;
  fallback?: ReactNode;
  children: (fieldValue: Uploadable | undefined, preview: string | undefined) => ReactNode;
  limitSize?: number;
}

export function UploadImageField<TF extends FieldValues>({
  disabled,
  name,
  control,
  children,
  fallback,
  className,
}: Props<TF>) {
  const { isIOS } = useUserPlatform();
  const { field } = useController({ control, name, disabled });
  const [preview, setPreview] = usePreviewImage();
  const { ref, pickMedia } = usePickMediaFile();
  const [show, , setShow] = useToggle();
  const { t } = useTranslation();
  const { getFileSizeLimit } = useFileSizeLimit();
  const fileSizeLimit = getFileSizeLimit('image');
  const isDesktop = useIsDesktop();

  return (
    <>
      {field.value ? (
        <button
          type='button'
          disabled={field.disabled}
          className={cn('btn btn-circle btn-ghost', className)}
          onClick={() => (isIOS || isDesktop ? pickMedia(undefined, FileType.Image) : setShow(true))}>
          {children(field.value, preview)}
        </button>
      ) : (
        <button
          type='button'
          className={cn('btn btn-circle btn-ghost size-14 bg-base-200', className)}
          onClick={() => (isIOS || isDesktop ? pickMedia(undefined, FileType.Image) : setShow(true))}>
          {fallback || <ImageIcon className='size-6' />}
        </button>
      )}

      <input
        type='file'
        className='sr-only'
        {...field}
        ref={(r) => {
          field.ref(r);
          ref.current = r;
        }}
        value=''
        onChange={(e) => {
          if (e.target.files?.[0]) {
            const file = e.target.files[0];
            if (fileSizeLimit && file.size > fileSizeLimit) {
              e.target.files = null;
              return addToast({
                type: 'error',
                message: t('errors.file-size-limit', { limit: readableFileSize(fileSizeLimit) }),
              });
            }
            setPreview(file);
            field.onChange(convertFileToUploadable(file));
          }
          setShow(false);
        }}
      />

      {show && (
        <Flyout
          show={show}
          onClose={() => setShow(false)}>
          <div className='flex flex-col items-stretch gap-y-2'>
            <div className='join join-vertical gap-y-px rounded-system-button'>
              {(['camera', 'album'] as const).map((item) => (
                <FlyoutMenuItem
                  joined
                  key={item}
                  label={t(`upload.${item}`)}
                  onClick={() => pickMedia(item, FileType.Image)}
                />
              ))}
            </div>
            <FlyoutMenuItem
              className='font-bold'
              label={t('common.cancel')}
              onClick={() => setShow(false)}
            />
          </div>
        </Flyout>
      )}
    </>
  );
}
